import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SpeakerIcon from '@mui/icons-material/Speaker';
import {
  getSettings,
  updateSettings,
  discoverBridges,
  testBridge,
  connectBridge,
  getLights,
  getRooms,
  getZones,
  getHueAuthStatus,
  getHueAuthUrl,
  disconnectHue,
} from '../services/api';
import CloudIcon from '@mui/icons-material/Cloud';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';

function Settings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [settings, setSettings] = useState({});
  const [bridges, setBridges] = useState([]);
  const [lights, setLights] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedLights, setSelectedLights] = useState([]);
  const [announceQuote, setAnnounceQuote] = useState(false);
  const [defaultGoogleHomeTarget, setDefaultGoogleHomeTarget] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [manualIp, setManualIp] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Remote API state
  const [isRemoteMode, setIsRemoteMode] = useState(false);
  const [remoteAuthStatus, setRemoteAuthStatus] = useState(null);

  useEffect(() => {
    loadSettings();
    loadHueAuthStatus();

    // Check for OAuth callback results in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('hue_auth_success')) {
      setSuccess('Successfully connected to Philips Hue!');
      window.history.replaceState({}, '', window.location.pathname);
      loadHueAuthStatus();
      loadLights();
      loadRoomsAndZones();
    } else if (urlParams.get('hue_auth_error')) {
      setError('Hue authentication failed: ' + urlParams.get('hue_auth_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadHueAuthStatus = async () => {
    try {
      const response = await getHueAuthStatus();
      setIsRemoteMode(response.data.isRemoteMode);
      setRemoteAuthStatus(response.data);

      // If authenticated, load lights, rooms, and zones
      if (response.data.authenticated) {
        await Promise.all([
          loadLights(),
          loadRoomsAndZones()
        ]);
      } else {
        // Clear data if not authenticated
        setLights([]);
        setRooms([]);
        setZones([]);
      }
    } catch (err) {
      console.error('Could not get auth status:', err);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response.data);

      if (response.data.selected_lights) {
        try {
          setSelectedLights(JSON.parse(response.data.selected_lights));
        } catch (e) {
          setSelectedLights([]);
        }
      }

      // Load announce quote setting
      setAnnounceQuote(response.data.announce_movie_quote === 'true' || response.data.announce_movie_quote === true);

      // Load default Google Home target
      setDefaultGoogleHomeTarget(response.data.default_google_home_target || 'all');
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const handleConnectRemoteHue = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getHueAuthUrl();
      // Redirect to Philips Hue OAuth page
      window.location.href = response.data.authUrl;
    } catch (err) {
      setError('Failed to start Hue authentication: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const handleDisconnectRemoteHue = async () => {
    try {
      setLoading(true);
      setError(null);
      await disconnectHue();
      setSuccess('Disconnected from Philips Hue');
      setRemoteAuthStatus({ authenticated: false, isRemoteMode: true });
      setLights([]);
      setRooms([]);
      setZones([]);
    } catch (err) {
      setError('Failed to disconnect: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLights = async () => {
    try {
      const response = await getLights();
      setLights(response.data || []);
    } catch (err) {
      console.error('Error loading lights:', err);
      setError('Failed to load lights: ' + (err.response?.data?.error || err.message));
    }
  };

  const loadRoomsAndZones = async () => {
    try {
      const [roomsResponse, zonesResponse] = await Promise.all([
        getRooms(),
        getZones(),
      ]);
      setRooms(roomsResponse.data || []);
      setZones(zonesResponse.data || []);
    } catch (err) {
      console.error('Error loading rooms/zones:', err);
      // Don't show error for rooms/zones as they're optional
      // Many users may not have rooms/zones configured
      setRooms([]);
      setZones([]);
    }
  };

  const handleDiscoverBridges = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const response = await discoverBridges();
      setBridges(response.data);

      if (response.data.length === 0) {
        setError('No Hue Bridges found via automatic discovery. Try entering your bridge IP manually below.');
        setShowManualEntry(true);
      } else {
        setSuccess(`Found ${response.data.length} bridge(s)!`);
        setShowManualEntry(false);
      }
    } catch (err) {
      setError('Failed to discover bridges: ' + err.message);
      setShowManualEntry(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTestManualIp = async () => {
    if (!manualIp || !manualIp.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) {
      setError('Please enter a valid IP address (e.g., 192.168.1.100)');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const response = await testBridge(manualIp);

      // Add manually discovered bridge to the list
      setBridges([response.data]);
      setSuccess(`Successfully found bridge: ${response.data.name} at ${response.data.ipaddress}`);
      setShowManualEntry(false);
    } catch (err) {
      setError('Failed to connect to bridge at ' + manualIp + '. Please verify the IP address is correct and the bridge is powered on.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectBridge = async (bridgeIp) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess('Please press the button on your Hue Bridge now...');

      await connectBridge(bridgeIp);
      setSuccess('Successfully connected to Hue Bridge!');

      // Reload settings, lights, rooms, and zones
      await loadSettings();
      await Promise.all([
        loadLights(),
        loadRoomsAndZones()
      ]);
    } catch (err) {
      setError('Failed to connect: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const updatedSettings = {
        ...settings,
        selected_lights: JSON.stringify(selectedLights),
        announce_movie_quote: announceQuote.toString(),
        default_google_home_target: defaultGoogleHomeTarget,
      };

      await updateSettings(updatedSettings);
      setSuccess('Settings saved successfully!');
      await loadSettings();
    } catch (err) {
      setError('Failed to save settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLight = (lightId) => {
    setSelectedLights(prev => {
      if (prev.includes(lightId)) {
        return prev.filter(id => id !== lightId);
      } else {
        return [...prev, lightId];
      }
    });
  };

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Hue Bridge Connection */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {isRemoteMode ? <CloudIcon color="primary" /> : null}
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Philips Hue {isRemoteMode ? '(Cloud Mode)' : 'Bridge'}
          </Typography>
        </Box>

        {/* Remote Mode UI */}
        {isRemoteMode ? (
          <>
            {remoteAuthStatus?.authenticated ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
                  Connected to Philips Hue via cloud
                  {lights.length > 0 && ` (${lights.length} lights found)`}
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button
                    variant="outlined"
                    onClick={async () => {
                      setLoading(true);
                      await Promise.all([loadLights(), loadRoomsAndZones()]);
                      setSuccess('Refreshed lights, rooms, and zones');
                      setLoading(false);
                    }}
                    disabled={loading}
                    fullWidth={isMobile}
                  >
                    Refresh Lights
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOffIcon />}
                    onClick={handleDisconnectRemoteHue}
                    disabled={loading}
                    fullWidth={isMobile}
                  >
                    Disconnect
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Connect to your Philips Hue account to control your lights from anywhere.
                </Alert>
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleConnectRemoteHue}
                  disabled={loading}
                  fullWidth={isMobile}
                  size="large"
                >
                  {loading ? <CircularProgress size={24} /> : 'Connect to Philips Hue'}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  You'll be redirected to Philips to authorize this app to control your lights.
                </Typography>
              </>
            )}
          </>
        ) : (
          /* Local Mode UI */
          <>
            {settings.hue_bridge_ip ? (
              <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
                Connected to bridge at {settings.hue_bridge_ip}
                {lights.length > 0 && ` (${lights.length} lights found)`}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No bridge connected. Click "Discover Bridges" to get started.
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2
              }}
            >
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={handleDiscoverBridges}
                disabled={loading}
                fullWidth={isMobile}
              >
                Discover Bridges
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowManualEntry(!showManualEntry)}
                disabled={loading}
                fullWidth={isMobile}
              >
                {showManualEntry ? 'Hide Manual Entry' : 'Enter IP Manually'}
              </Button>
            </Box>

            {showManualEntry && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Manual Bridge IP Entry
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Find your bridge IP in your router's DHCP client list or in the Philips Hue mobile app under Settings → Bridge Settings.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2
                  }}
                >
                  <TextField
                    label="Bridge IP Address"
                    value={manualIp}
                    onChange={(e) => setManualIp(e.target.value)}
                    placeholder="192.168.1.100"
                    size="small"
                    sx={{ flex: 1 }}
                    fullWidth={isMobile}
                  />
                  <Button
                    variant="contained"
                    onClick={handleTestManualIp}
                    disabled={loading || !manualIp}
                    fullWidth={isMobile}
                  >
                    Test Connection
                  </Button>
                </Box>
              </Box>
            )}

            {bridges.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Found Bridges:
                </Typography>
                <List>
                  {bridges.map((bridge) => (
                    <ListItem
                      key={bridge.ipaddress}
                      sx={{
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1, sm: 0 }
                      }}
                      secondaryAction={
                        !isMobile && (
                          <Button
                            variant="contained"
                            onClick={() => handleConnectBridge(bridge.ipaddress)}
                            disabled={loading}
                          >
                            Connect
                          </Button>
                        )
                      }
                    >
                      <ListItemText
                        primary={bridge.name}
                        secondary={bridge.ipaddress}
                      />
                      {isMobile && (
                        <Button
                          variant="contained"
                          onClick={() => handleConnectBridge(bridge.ipaddress)}
                          disabled={loading}
                          fullWidth
                        >
                          Connect
                        </Button>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Light Selection */}
      {lights.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Select Lights to Control
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose which lights should respond to movie themes. Leave all unchecked to control all lights.
          </Typography>
          <FormGroup>
            {lights.map((light) => (
              <FormControlLabel
                key={light.id}
                control={
                  <Checkbox
                    checked={selectedLights.includes(light.id)}
                    onChange={() => handleToggleLight(light.id)}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {light.name} ({light.type})
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Paper>
      )}

      {/* Google Home Settings */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SpeakerIcon color="primary" />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Google Home
          </Typography>
        </Box>

        {/* Default Target Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="default-target-label">Default Light Target</InputLabel>
            <Select
              labelId="default-target-label"
              value={defaultGoogleHomeTarget}
              label="Default Light Target"
              onChange={(e) => setDefaultGoogleHomeTarget(e.target.value)}
            >
              <MenuItem value="all">All Lights</MenuItem>
              <MenuItem value="selected" disabled={selectedLights.length === 0}>
                Selected Lights {selectedLights.length > 0 ? `(${selectedLights.length})` : '(none selected)'}
              </MenuItem>
              {lights.length > 0 && (
                <>
                  <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 'bold' }}>— Individual Lights —</MenuItem>
                  {lights.map((light) => (
                    <MenuItem key={`light-${light.id}`} value={`light:${light.id}`}>
                      {light.name}
                    </MenuItem>
                  ))}
                </>
              )}
              {rooms.length > 0 && <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 'bold' }}>— Rooms —</MenuItem>}
              {rooms.map((room) => (
                <MenuItem key={`room-${room.id}`} value={`room:${room.id}`}>
                  {room.name}
                </MenuItem>
              ))}
              {zones.length > 0 && <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 'bold' }}>— Zones —</MenuItem>}
              {zones.map((zone) => (
                <MenuItem key={`zone-${zone.id}`} value={`zone:${zone.id}`}>
                  {zone.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Choose which lights Google Home should control when you say "Hey Google, let's watch [movie]".
          </Typography>
          {lights.length === 0 && rooms.length === 0 && zones.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              No lights, rooms, or zones found. Make sure your Hue connection is active and try refreshing the page.
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Movie Quote Setting */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FormatQuoteIcon color="primary" />
          <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Movie Quotes
          </Typography>
        </Box>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={announceQuote}
                onChange={(e) => setAnnounceQuote(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Announce famous movie quote
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 4 }, mt: 0.5 }}>
            When enabled, your Google Home will announce an iconic quote from the movie after setting the theme lights.
            For example, when you say "Hey Google, let's watch Star Wars", it will respond with "May the force be with you."
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: { xs: 0, sm: 4 }, mt: 1 }}>
            Requires OPENAI_API_KEY to be configured in your environment variables.
          </Typography>
        </FormGroup>
      </Paper>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
        <Button
          variant="contained"
          onClick={handleSaveSettings}
          disabled={loading}
          size="large"
          fullWidth={isMobile}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
}

export default Settings;
